import React, { useState, useEffect, useRef, useCallback } from 'react';
import { storage } from './lib/storage';

/**
 * CrossTrainer Component
 * 
 * A dedicated practice tool for speedcubers to improve their cross solving.
 * Features:
 * - Cross-focused scramble generation
 * - WCA-compliant inspection time (15s with penalties)
 * - Timer with spacebar controls
 * - Move counting to track efficiency
 * - Statistics (Best, Average, Ao5, Ao12)
 * - Session tracking with localStorage persistence
 */

// Scramble generation utility
const generateScramble = () => {
  const moves = ["R", "L", "U", "D", "F", "B"];
  const modifiers = ["", "'", "2"];
  let scramble = [];
  let lastMove = "";

  for (let i = 0; i < 20; i++) {
    let move;
    do {
      move = moves[Math.floor(Math.random() * moves.length)];
    } while (move === lastMove);

    const modifier = modifiers[Math.floor(Math.random() * modifiers.length)];
    scramble.push(move + modifier);
    lastMove = move;
  }

  return scramble.join(" ");
};

// Generate a plausible cross solution with target move count
const generateCrossSolution = (targetMoves = null) => {
  const moves = ["R", "L", "U", "D", "F", "B", "R2", "L2", "U2", "D2", "F2", "B2", "R'", "L'", "U'", "D'", "F'", "B'"];
  const crossMoves = ["D", "D'", "D2", "R", "R'", "R2", "L", "L'", "L2", "F", "F'", "F2", "B", "B'", "B2"];

  const moveCount = targetMoves || (4 + Math.floor(Math.random() * 4)); // 4-7 moves if not specified
  let solution = [];
  let lastMove = "";

  for (let i = 0; i < moveCount; i++) {
    let move;
    do {
      move = crossMoves[Math.floor(Math.random() * crossMoves.length)];
    } while (move[0] === lastMove[0]); // Avoid same face twice in a row

    solution.push(move);
    lastMove = move;
  }

  return { solution: solution.join(" "), moveCount };
};

const CrossTrainer = ({ practiceTimes, setPracticeTimes }) => {
  // Timer states
  const [isRunning, setIsRunning] = useState(false);
  const [isHolding, setIsHolding] = useState(false);
  const [time, setTime] = useState(0);
  const [moveCount, setMoveCount] = useState('');
  const [scramble, setScramble] = useState('');

  // Cross solution states
  const [crossSolution, setCrossSolution] = useState({ solution: '', moveCount: 0 });
  const [showSolution, setShowSolution] = useState(false);
  const [targetMoveCount, setTargetMoveCount] = useState(null); // null = any, or 4, 5, 6, 7

  // WCA Inspection states
  const [inspectionEnabled, setInspectionEnabled] = useState(true);
  const [isInspecting, setIsInspecting] = useState(false);
  const [inspectionTime, setInspectionTime] = useState(15);
  const [penalty, setPenalty] = useState(null); // null, 'plus2', or 'dnf'
  
  // Refs to track current values (prevents closure issues)
  const isRunningRef = useRef(false);
  const isHoldingRef = useRef(false);
  const isInspectingRef = useRef(false);
  const timeRef = useRef(0);
  const inspectionTimeRef = useRef(15);
  const practiceTimesRef = useRef(practiceTimes);
  const moveCountRef = useRef('');
  const penaltyRef = useRef(null);
  const holdTimeoutRef = useRef(null);
  const intervalRef = useRef(null);
  const inspectionIntervalRef = useRef(null);

  const crossTimes = practiceTimes?.cross || [];

  // Keep refs in sync with state
  useEffect(() => { isRunningRef.current = isRunning; }, [isRunning]);
  useEffect(() => { isHoldingRef.current = isHolding; }, [isHolding]);
  useEffect(() => { isInspectingRef.current = isInspecting; }, [isInspecting]);
  useEffect(() => { timeRef.current = time; }, [time]);
  useEffect(() => { inspectionTimeRef.current = inspectionTime; }, [inspectionTime]);
  useEffect(() => { practiceTimesRef.current = practiceTimes; }, [practiceTimes]);
  useEffect(() => { moveCountRef.current = moveCount; }, [moveCount]);
  useEffect(() => { penaltyRef.current = penalty; }, [penalty]);

  // Generate initial scramble on mount
  useEffect(() => {
    setScramble(generateScramble());
    const solution = generateCrossSolution(targetMoveCount);
    setCrossSolution(solution);
  }, []);

  // Regenerate solution when target changes
  useEffect(() => {
    const solution = generateCrossSolution(targetMoveCount);
    setCrossSolution(solution);
  }, [targetMoveCount]);

  const newScramble = () => {
    setScramble(generateScramble());
    const solution = generateCrossSolution(targetMoveCount);
    setCrossSolution(solution);
    setShowSolution(false);
    setTime(0);
    timeRef.current = 0;
    setPenalty(null);
    penaltyRef.current = null;
    setInspectionTime(15);
    inspectionTimeRef.current = 15;
  };

  const formatTime = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const milliseconds = Math.floor((ms % 1000) / 10);
    return `${seconds}.${milliseconds.toString().padStart(2, '0')}`;
  };

  // Solve timer interval
  useEffect(() => {
    if (isRunning) {
      intervalRef.current = setInterval(() => {
        setTime(t => {
          timeRef.current = t + 10;
          return t + 10;
        });
      }, 10);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isRunning]);

  // Inspection timer interval
  useEffect(() => {
    if (isInspecting) {
      inspectionIntervalRef.current = setInterval(() => {
        setInspectionTime(t => {
          const newTime = t - 0.01;
          inspectionTimeRef.current = newTime;
          
          // Apply penalties
          if (newTime <= 0 && newTime > -2) {
            setPenalty('plus2');
            penaltyRef.current = 'plus2';
          } else if (newTime <= -2) {
            setPenalty('dnf');
            penaltyRef.current = 'dnf';
            // Auto-stop inspection at DNF
            setIsInspecting(false);
            setInspectionTime(0);
            return 0;
          }
          
          return newTime;
        });
      }, 10);
    } else {
      if (inspectionIntervalRef.current) {
        clearInterval(inspectionIntervalRef.current);
      }
    }
    return () => {
      if (inspectionIntervalRef.current) {
        clearInterval(inspectionIntervalRef.current);
      }
    };
  }, [isInspecting]);

  const saveCrossTime = useCallback((finalTime) => {
    const currentPracticeTimes = practiceTimesRef.current;
    const currentCrossTimes = currentPracticeTimes?.cross || [];
    
    const newEntry = {
      time: finalTime,
      date: new Date().toISOString(),
      moves: moveCountRef.current ? parseInt(moveCountRef.current) : null,
      penalty: penaltyRef.current
    };
    
    const updatedTimes = {
      ...currentPracticeTimes,
      cross: [...currentCrossTimes, newEntry]
    };
    
    setPracticeTimes(updatedTimes);

    // Save to storage
    storage.savePracticeTimes(updatedTimes);

    setMoveCount('');
    newScramble();
  }, [setPracticeTimes]);

  const handleKeyDown = useCallback((e) => {
    if (e.code !== 'Space' || e.target.tagName === 'INPUT') return;
    e.preventDefault();
    if (e.repeat) return;

    // Stop solve timer if running
    if (isRunningRef.current) {
      setIsRunning(false);
      const finalTime = timeRef.current;
      const currentPenalty = penaltyRef.current;
      
      let adjustedTime = finalTime;
      if (currentPenalty === 'plus2') {
        adjustedTime = finalTime + 2000;
      }
      
      if (currentPenalty !== 'dnf') {
        saveCrossTime(adjustedTime);
      } else {
        // Save DNF
        saveCrossTime(null);
      }
      return;
    }

    // Stop inspection and start solve if inspecting
    if (isInspectingRef.current) {
      setIsInspecting(false);
      setIsHolding(true);
      holdTimeoutRef.current = setTimeout(() => {
        setIsHolding(false);
        setIsRunning(true);
        setTime(0);
        timeRef.current = 0;
      }, 300);
      return;
    }

    // Start inspection or solve
    if (!isRunningRef.current && !isInspectingRef.current) {
      setIsHolding(true);
      holdTimeoutRef.current = setTimeout(() => {
        setIsHolding(false);
        if (inspectionEnabled) {
          setIsInspecting(true);
          setInspectionTime(15);
          inspectionTimeRef.current = 15;
          setPenalty(null);
          penaltyRef.current = null;
        } else {
          setIsRunning(true);
          setTime(0);
          timeRef.current = 0;
        }
      }, 300);
    }
  }, [inspectionEnabled, saveCrossTime]);

  const handleKeyUp = useCallback((e) => {
    if (e.code !== 'Space') return;
    e.preventDefault();

    if (isHoldingRef.current) {
      setIsHolding(false);
      if (holdTimeoutRef.current) {
        clearTimeout(holdTimeoutRef.current);
      }
    }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      if (holdTimeoutRef.current) clearTimeout(holdTimeoutRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (inspectionIntervalRef.current) clearInterval(inspectionIntervalRef.current);
    };
  }, [handleKeyDown, handleKeyUp]);

  // Calculate statistics
  const calculateStats = () => {
    const validTimes = crossTimes.filter(t => t.penalty !== 'dnf' && t.time !== null);
    
    if (validTimes.length === 0) {
      return { best: null, avg: null, ao5: null, ao12: null, count: 0, dnfCount: 0 };
    }

    const times = validTimes.map(t => t.time);
    const best = Math.min(...times);
    const avg = times.reduce((sum, t) => sum + t, 0) / times.length;

    const calculateAverage = (arr, n) => {
      if (arr.length < n) return null;
      const slice = arr.slice(-n);
      const sorted = [...slice].sort((a, b) => a - b);
      const trimmed = sorted.slice(1, -1);
      return trimmed.reduce((a, b) => a + b, 0) / trimmed.length;
    };

    const dnfCount = crossTimes.filter(t => t.penalty === 'dnf').length;

    return {
      best,
      avg,
      ao5: calculateAverage(times, 5),
      ao12: calculateAverage(times, 12),
      count: crossTimes.length,
      dnfCount
    };
  };

  const stats = calculateStats();

  // Get inspection color based on time remaining
  const getInspectionColor = () => {
    if (inspectionTime > 8) return '#4ade80'; // green
    if (inspectionTime > 0) return '#fbbf24'; // yellow
    return '#ef4444'; // red
  };

  return (
    <div style={{
      background: 'var(--bg-tertiary, #1e293b)',
      borderRadius: '12px',
      padding: '1.5rem',
      marginTop: '1rem',
      border: '1px solid var(--border-color, #334155)'
    }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '1rem' 
      }}>
        <h4 style={{ margin: 0, color: 'var(--accent-primary, #3b82f6)' }}>
          ⏱️ Cross Time Trainer
        </h4>
        <label style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '0.5rem',
          color: 'var(--text-secondary, #94a3b8)',
          fontSize: '0.9rem',
          cursor: 'pointer'
        }}>
          <input
            type="checkbox"
            checked={inspectionEnabled}
            onChange={(e) => setInspectionEnabled(e.target.checked)}
          />
          WCA Inspection (15s)
        </label>
      </div>
      
      {/* Timer Display */}
      <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
        {isInspecting ? (
          <div style={{
            fontFamily: "'Roboto Mono', monospace",
            fontSize: '3rem',
            fontWeight: 700,
            color: getInspectionColor(),
            transition: 'color 0.2s'
          }}>
            {Math.ceil(inspectionTime)}
          </div>
        ) : (
          <div style={{
            fontFamily: "'Roboto Mono', monospace",
            fontSize: '3rem',
            fontWeight: 700,
            color: isHolding ? 'var(--accent-secondary, #10b981)' : 
                   isRunning ? 'var(--text-primary, #f1f5f9)' : 
                   'var(--accent-primary, #3b82f6)',
            transition: 'color 0.2s'
          }}>
            {formatTime(time)}s
            {penalty === 'plus2' && <span style={{ fontSize: '1.5rem', color: '#fbbf24' }}>+2</span>}
            {penalty === 'dnf' && <span style={{ fontSize: '1.5rem', color: '#ef4444' }}>DNF</span>}
          </div>
        )}
        <div style={{ color: 'var(--text-muted, #64748b)', fontSize: '0.9rem' }}>
          {isInspecting ? 'Hold SPACE to start solving' :
           isRunning ? 'Press SPACE to stop' : 
           'Hold SPACE to start'}
        </div>
      </div>

      {/* Scramble */}
      <div style={{
        background: 'var(--bg-secondary, #0f172a)',
        padding: '1rem',
        borderRadius: '8px',
        marginBottom: '1rem'
      }}>
        <div style={{ 
          color: 'var(--text-secondary, #94a3b8)', 
          fontSize: '0.8rem',
          marginBottom: '0.5rem' 
        }}>
          Scramble
        </div>
        <div style={{
          fontFamily: "'Roboto Mono', monospace",
          fontSize: '1rem',
          color: 'var(--text-primary, #f1f5f9)',
          marginBottom: '0.75rem'
        }}>
          {scramble}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button
            onClick={newScramble}
            style={{
              padding: '0.5rem 1rem',
              background: 'var(--accent-primary, #3b82f6)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            🔄 New Scramble
          </button>
          <button
            onClick={() => setShowSolution(!showSolution)}
            style={{
              padding: '0.5rem 1rem',
              background: showSolution ? 'var(--success, #22c55e)' : 'var(--bg-tertiary, #334155)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            {showSolution ? '👁️ Hide Solution' : '👁️ Show Solution'}
          </button>
        </div>
      </div>

      {/* Target Move Count Filter */}
      <div style={{ marginBottom: '1rem' }}>
        <label style={{
          display: 'block',
          color: 'var(--text-secondary, #94a3b8)',
          fontSize: '0.9rem',
          marginBottom: '0.5rem'
        }}>
          Practice Target (moves):
        </label>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {[null, 4, 5, 6, 7].map(count => (
            <button
              key={count || 'any'}
              onClick={() => {
                setTargetMoveCount(count);
                setShowSolution(false);
              }}
              style={{
                padding: '0.5rem 1rem',
                background: targetMoveCount === count ? 'var(--accent-primary, #3b82f6)' : 'var(--bg-secondary, #0f172a)',
                color: targetMoveCount === count ? 'white' : 'var(--text-secondary, #94a3b8)',
                border: `1px solid ${targetMoveCount === count ? 'var(--accent-primary, #3b82f6)' : 'var(--border-color, #334155)'}`,
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: targetMoveCount === count ? '600' : '400'
              }}
            >
              {count || 'Any'}
            </button>
          ))}
        </div>
      </div>

      {/* Cross Solution Display */}
      {showSolution && (
        <div style={{
          background: 'var(--bg-secondary, #0f172a)',
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '1rem',
          border: '2px solid var(--success, #22c55e)'
        }}>
          <div style={{
            color: 'var(--success, #22c55e)',
            fontSize: '0.8rem',
            fontWeight: '600',
            marginBottom: '0.5rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            💡 Suggested Cross Solution ({crossSolution.moveCount} moves)
          </div>
          <div style={{
            fontFamily: "'Roboto Mono', monospace",
            fontSize: '1.1rem',
            color: 'var(--text-primary, #f1f5f9)',
            fontWeight: '500'
          }}>
            {crossSolution.solution}
          </div>
          <div style={{
            color: 'var(--text-muted, #64748b)',
            fontSize: '0.75rem',
            marginTop: '0.5rem',
            fontStyle: 'italic'
          }}>
            Note: This is a generated example solution. Actual optimal may vary.
          </div>
        </div>
      )}

      {/* Move Counter */}
      <div style={{ marginBottom: '1rem' }}>
        <label style={{
          display: 'block',
          color: 'var(--text-secondary, #94a3b8)',
          fontSize: '0.9rem',
          marginBottom: '0.5rem'
        }}>
          Move Count (optional):
        </label>
        <input
          type="number"
          value={moveCount}
          onChange={(e) => setMoveCount(e.target.value)}
          placeholder="e.g., 8"
          style={{
            width: '100%',
            padding: '0.5rem',
            background: 'var(--bg-secondary, #0f172a)',
            border: '1px solid var(--border-color, #334155)',
            borderRadius: '6px',
            color: 'var(--text-primary, #f1f5f9)',
            fontSize: '1rem'
          }}
        />
      </div>

      {/* Statistics */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
        gap: '0.75rem',
        marginBottom: '1rem'
      }}>
        {[
          { label: 'Best', value: stats.best },
          { label: 'Average', value: stats.avg },
          { label: 'Ao5', value: stats.ao5 },
          { label: 'Ao12', value: stats.ao12 }
        ].map(({ label, value }) => (
          <div 
            key={label}
            style={{
              background: 'var(--bg-secondary, #0f172a)',
              padding: '0.75rem',
              borderRadius: '8px',
              textAlign: 'center'
            }}
          >
            <div style={{ 
              color: 'var(--text-muted, #64748b)', 
              fontSize: '0.8rem',
              marginBottom: '0.25rem'
            }}>
              {label}
            </div>
            <div style={{
              fontFamily: "'Roboto Mono', monospace",
              fontSize: '1.1rem',
              color: 'var(--text-primary, #f1f5f9)',
              fontWeight: 600
            }}>
              {value ? `${formatTime(value)}s` : '-'}
            </div>
          </div>
        ))}
      </div>

      {/* Solve Count */}
      <div style={{ 
        textAlign: 'center',
        color: 'var(--text-secondary, #94a3b8)',
        fontSize: '0.9rem'
      }}>
        Solves: {stats.count} {stats.dnfCount > 0 && `(${stats.dnfCount} DNF)`}
      </div>

      {/* Recent Times */}
      {crossTimes.length > 0 && (
        <div style={{ marginTop: '1rem' }}>
          <div style={{
            color: 'var(--text-secondary, #94a3b8)',
            fontSize: '0.9rem',
            marginBottom: '0.5rem'
          }}>
            Recent Times
          </div>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.5rem'
          }}>
            {crossTimes.slice(-10).reverse().map((entry, idx) => (
              <span
                key={idx}
                style={{
                  padding: '0.25rem 0.5rem',
                  background: entry.penalty === 'dnf' ? 'var(--error, #ef4444)20' :
                             entry.time === stats.best ? 'var(--success, #22c55e)20' : 
                             'var(--bg-secondary, #0f172a)',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  fontFamily: "'Roboto Mono', monospace",
                  color: entry.penalty === 'dnf' ? 'var(--error, #ef4444)' :
                         entry.time === stats.best ? 'var(--success, #22c55e)' : 
                         'var(--text-primary, #f1f5f9)',
                  border: `1px solid ${entry.time === stats.best ? 'var(--success, #22c55e)' : 'var(--border-color, #334155)'}`
                }}
              >
                {entry.penalty === 'dnf' ? 'DNF' : 
                 `${formatTime(entry.time)}${entry.penalty === 'plus2' ? '+' : ''}`}
                {entry.moves && (
                  <span style={{ 
                    color: 'var(--text-muted, #64748b)', 
                    marginLeft: '4px', 
                    fontSize: '0.8rem' 
                  }}>
                    ({entry.moves})
                  </span>
                )}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CrossTrainer;
